/**
 * Paths that are excluded from auth middleware (no login required).
 * Shared by auth.global middleware and useCPRouteContext for consistent behavior.
 */
export const CP_AUTH_EXCLUDED_PATHS = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/login-with-code',
  '/auth',
] as const

export const CP_AUTH_EXCLUDED_PATHS_SET = new Set<string>(CP_AUTH_EXCLUDED_PATHS)

/**
 * Public checkout route prefixes.
 * Dynamic routes:
 * - `/courses/offers/:offerId`
 * - `/courses/checkout/:offerId` (Course Default Pricing checkout link)
 * - `/courses/upsell-offers/one-click/:upsellId`
 */
export const CP_PUBLIC_CHECKOUT_ROUTE_PREFIXES = ['/courses/offers/', '/courses/checkout/', '/courses/upsell-offers/one-click/'] as const

export const isCPPublicCheckoutRoutePath = (path?: string): boolean =>
  typeof path === 'string' && CP_PUBLIC_CHECKOUT_ROUTE_PREFIXES.some(prefix => path.startsWith(prefix))

/**
 * `/expired-password` is included so the post-login migration gate (set by
 * `saveAuthAfterLogin` when `migrationStatus = CREATED`) is reachable by an
 * already-authenticated user without being bounced to /dashboard.
 *
 * /login and /signup are intentionally NOT in this list — authenticated users on
 * those pages SHOULD be bounced to /dashboard (with the explicit one-shot
 * `consumeAuthenticatedLoginVisit` escape hatch for "back to login" intents).
 */
export const CP_AUTH_PUBLIC_FLOW_PATHS = [
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/login-with-code',
  '/expired-password',
] as const

export const CP_AUTH_PUBLIC_FLOW_PATHS_SET = new Set<string>(CP_AUTH_PUBLIC_FLOW_PATHS)

export const CP_AUTH_FULL_SCREEN_PATHS = ['/expired-password', '/profile-confirmation'] as const

export const CP_AUTH_FULL_SCREEN_PATHS_SET = new Set<string>(CP_AUTH_FULL_SCREEN_PATHS)

export { requestAuthenticatedLoginVisit, consumeAuthenticatedLoginVisit } from '@common/auth-layer/utils/authenticated-login-visit'

export { MIGRATION_GATE_PATHS, MIGRATION_GATE_PATHS_SET } from '@common/auth-layer/utils/migration'
